<div class="cover">
    <qpx-building-scenery [propertyType]="details()?.primaryPropertyType" />
    @if (isDraft()) {
        <div class="draft-banner">
            <qpx-hero-icon
                name="lucidePencil"
                context="caution"
                [active]="true"
            />
            <output qpxBody="caution" tone="muted" size="md" aria-label="Draft">
                <strong>Draft</strong>
                <span>
                    Some capabilities are available only after publishing this
                    building.
                </span>
            </output>
        </div>
    } @else if (warningUsage(); as usage) {
        <qpx-preview-storage-capacity-banner [storageUsage]="usage" />
    }
</div>

<header class="header">
    <h2 qpxHeading [id]="headingId">
        {{ displayBuilding() | qpxBuildingName }}
    </h2>

    <div class="actions">
        <button
            qpxButton
            size="sm"
            matButton="outlined"
            [attr.aria-label]="
                ['Open', displayBuilding() | qpxBuildingName].join(' ')
            "
            (click)="openRequested.emit()"
        >
            <ng-icon qpx size="md" name="lucideArrowUpRight" matButtonIcon />
            <span>Open</span>
        </button>
        @if (details()) {
            @if (canPublish()) {
                <button
                    qpxButton
                    size="sm"
                    matButton="outlined"
                    (click)="publishBuilding()"
                >
                    <ng-icon qpx name="lucideCloudUpload" matButtonIcon />
                    <span>Publish</span>
                </button>
            }
            @if (canDelete()) {
                <button
                    qpxButton
                    size="sm"
                    context="destructive"
                    matIconButton
                    [attr.aria-label]="`Delete ${displayBuilding().name}`"
                    qpxTooltip="Delete building"
                    (click)="deleteBuilding()"
                >
                    <ng-icon qpx context="destructive" name="lucideTrash2" />
                </button>
            }
        }
    </div>

    <div #subtitle class="subtitle" qpxBody="secondary" tone="muted">
        <span>
            {{ displayBuilding().address | qpxAddress: 'city' }}
        </span>
        @if (details(); as buildingDetails) {
            <span class="separator" aria-hidden="true">·</span>
            @if (buildingDetails.subPremiseTypeName; as subPremiseTypeName) {
                <qpx-building-identity-fact
                    geoscapeLabel="Geoscape"
                    [label]="subPremiseTypeIdentity.label"
                    [value]="subPremiseTypeName"
                    [icon]="
                        buildingDetails.subPremiseType
                            | qpxBuildingSubPremiseTypeIcon
                    "
                    [definition]="subPremiseTypeIdentity.definition"
                    [definitionCitationLabel]="
                        subPremiseTypeIdentity.citationLabel
                    "
                    [definitionCitationUrl]="subPremiseTypeIdentity.citationUrl"
                    [source]="subPremiseTypeIdentity.source"
                    [sourceDescription]="
                        subPremiseTypeIdentity.sourceDescription
                    "
                    [sourceCitationUrl]="subPremiseTypeIdentity.citationUrl"
                />
            } @else {
                <qpx-building-identity-fact
                    geoscapeLabel="Geoscape"
                    [label]="propertyTypeIdentity.label"
                    [value]="buildingDetails.propertyType"
                    [icon]="
                        buildingDetails.primaryPropertyType
                            | qpxBuildingPropertyTypeIcon
                    "
                    [definition]="propertyTypeIdentity.definition"
                    [definitionCitationLabel]="
                        propertyTypeIdentity.citationLabel
                    "
                    [definitionCitationUrl]="propertyTypeIdentity.citationUrl"
                    [source]="propertyTypeIdentity.source"
                />
            }
            @if (binEnabled()) {
                <span
                    #binMeasure
                    class="bin-measure"
                    aria-hidden="true"
                    [attr.data-value]="
                        buildingDetails.buildingIdentificationNumbers
                    "
                ></span>
                <span class="separator" aria-hidden="true">·</span>
                <qpx-building-identity-fact
                    #binFact
                    [iconOnly]="compactBin()"
                    label="Building Identification Number (BIN)"
                    [value]="buildingDetails.buildingIdentificationNumbers"
                    [icon]="
                        buildingDetails.buildingIdentificationNumbers ===
                        'Not yet available'
                            ? 'qpxQrCodeOff'
                            : 'lucideQrCode'
                    "
                    definition="Number identifying the building in its permanent record."
                    definitionCitationLabel="Quipex article"
                    definitionCitationUrl="https://www.quipex.au/blog/why-every-building-needs-a-permanent-record"
                    source="geoscape"
                    [showSource]="
                        buildingDetails.buildingIdentificationNumbers !==
                        'Not yet available'
                    "
                />
            }
        } @else if (isLoading()) {
            <span class="separator" aria-hidden="true">·</span>
            <qpx-skeleton-text class="max-w-36" text="body-md" />
        }
    </div>
</header>

@if (isLoading()) {
    <output class="sr-only" aria-label="Loading building details">
        Loading building details
    </output>
    <div class="metadata-grid">
        @for (tile of loadingTiles; track tile) {
            <div class="metadata-item" aria-hidden="true">
                <qpx-skeleton
                    width="2.5rem"
                    height="2rem"
                    borderRadius="var(--qpx-radius-sm)"
                />
                <div class="metadata-copy grid min-w-0 flex-1 gap-0.5">
                    <qpx-skeleton-text width="55%" text="body-sm" />
                    <qpx-skeleton-text width="72%" text="body-md" />
                </div>
            </div>
        }
    </div>
} @else if (error(); as loadError) {
    <qpx-error-state
        class="details-error"
        role="alert"
        [error]="loadError"
        heading="Unable to load building details"
    >
        <button qpxButton size="sm" matButton="outlined" (click)="retry()">
            <ng-icon qpx name="lucideRefreshCw" matButtonIcon />
            <span>Retry</span>
        </button>
    </qpx-error-state>
} @else if (details(); as buildingDetails) {
    <div class="metadata-grid" aria-label="Building information">
        @for (item of buildingDetails.metadata; track item.label) {
            <div qpx-focused-building-preview-fact [item]="item"></div>
        }
    </div>
}
